import React, { useState } from 'react';
import { CircularProgress } from '@mui/material';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    TablePagination, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Button,
    Divider, Chip, useMediaQuery,
} from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import { toast } from 'react-toastify';
import baseURL from '../../config';

const MS_COLORS = {
    primary: '#2D4A3D',
    primaryLight: '#EBF5EE',
    text: '#323130',
    textSecondary: '#605D5C',
    background: '#FFFFFF',
    backgroundLight: '#F3F2F1',
    border: '#E1E1E1',
    hoverBackground: '#F0F0F0',
    white: '#FFFFFF',
    accent: '#107C10',
    red: '#D83B01',
};

const headerCellSx = { fontWeight: 'bold', fontSize: '0.85rem', whiteSpace: 'nowrap', color: MS_COLORS.text };
const bodyCellSx = { fontSize: '0.8rem', color: MS_COLORS.textSecondary, whiteSpace: 'nowrap' };

const statusChip = (status) => {
    const map = {
        pending:  { label: 'Pending',  color: '#FF8C00', bg: '#FFF4E5' },
        approved: { label: 'Approved', color: MS_COLORS.accent, bg: '#EBF5EB' },
        rejected: { label: 'Rejected', color: MS_COLORS.red, bg: '#FEF0EB' },
    };
    const s = map[status] || { label: status, color: MS_COLORS.text, bg: '#F3F2F1' };
    return (
        <Chip
            label={s.label}
            size="small"
            sx={{ backgroundColor: s.bg, color: s.color, fontWeight: 'bold', fontSize: '0.72rem', height: 22 }}
        />
    );
};

const UnsubscribeRequestsTable = ({ requests, setRequests }) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [processingId, setProcessingId] = useState(null);

    // Reject dialog state
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectNotes, setRejectNotes] = useState('');
    const [selectedId, setSelectedId] = useState(null);

    const isSmallScreen = useMediaQuery('(max-width:600px)');

    const handleApprove = async (id) => {
        setProcessingId(id);
        try {
            const response = await fetch(`${baseURL}/admin/unsubscribe-requests/${id}/approve`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Failed to approve request');
            setRequests((prev) => prev.map((r) => r.id === id ? { ...r, status: 'approved' } : r));
            toast.success('Unsubscribe request approved and subscription cancelled.');
        } catch (err) {
            toast.error(err.message || 'Failed to approve request.');
        } finally {
            setProcessingId(null);
        }
    };

    const handleRejectClick = (id) => {
        setSelectedId(id);
        setRejectNotes('');
        setRejectOpen(true);
    };

    const handleRejectSubmit = async () => {
        setProcessingId(selectedId);
        try {
            const response = await fetch(`${baseURL}/admin/unsubscribe-requests/${selectedId}/reject`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ notes: rejectNotes }),
                credentials: 'include',
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Failed to reject request');
            setRequests((prev) => prev.map((r) => r.id === selectedId ? { ...r, status: 'rejected' } : r));
            toast.info('Unsubscribe request rejected.');
            setRejectOpen(false);
        } catch (err) {
            toast.error(err.message || 'Failed to reject request.');
        } finally {
            setProcessingId(null);
        }
    };

    return (
        <Box
            sx={{
                p: 2,
                backgroundColor: MS_COLORS.background,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                mt: 3,
                border: `1px solid ${MS_COLORS.border}`,
            }}
        >
            <Typography
                variant="h6"
                sx={{
                    color: MS_COLORS.text,
                    mb: 3,
                    fontWeight: 'bold',
                    fontSize: isSmallScreen ? '1.1rem' : '1.4rem',
                }}
            >
                Unsubscribe Requests
            </Typography>

            {requests.length === 0 ? (
                <Box
                    sx={{
                        textAlign: 'center',
                        mt: 3,
                        p: 3,
                        border: `1px dashed ${MS_COLORS.border}`,
                        borderRadius: 1,
                        bgcolor: MS_COLORS.backgroundLight,
                    }}
                >
                    <Typography sx={{ color: MS_COLORS.textSecondary, fontSize: '0.9rem' }}>
                        No unsubscribe requests at this time.
                    </Typography>
                </Box>
            ) : (
                <>
                    <TableContainer
                        component={Paper}
                        sx={{
                            borderRadius: 1,
                            boxShadow: 0,
                            overflowX: 'auto',
                            border: `1px solid ${MS_COLORS.border}`,
                        }}
                    >
                        <Table sx={{ minWidth: 900 }}>
                            <TableHead sx={{ bgcolor: MS_COLORS.backgroundLight }}>
                                <TableRow>
                                    {[
                                        'Donor Name', 'Donor Email', 'Campaign', 'Amount (UGX)',
                                        'Frequency', 'Date Requested', 'Status', 'Actions',
                                    ].map((h) => (
                                        <TableCell key={h} sx={headerCellSx}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {requests
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((req) => (
                                        <TableRow
                                            key={req.id}
                                            sx={{
                                                '&:last-child td, &:last-child th': { border: 0 },
                                                '&:hover': { backgroundColor: MS_COLORS.hoverBackground },
                                            }}
                                        >
                                            <TableCell sx={bodyCellSx}>{req.donor_name}</TableCell>
                                            <TableCell sx={bodyCellSx}>{req.donor_email}</TableCell>
                                            <TableCell sx={bodyCellSx}>{req.campaign_name}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                UGX {Number(req.amount).toLocaleString()}
                                            </TableCell>
                                            <TableCell sx={{ ...bodyCellSx, textTransform: 'capitalize' }}>
                                                {req.frequency}
                                            </TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                {new Date(req.created_at).toLocaleDateString('en-GB')}
                                            </TableCell>
                                            <TableCell sx={{ py: 1 }}>{statusChip(req.status)}</TableCell>
                                            <TableCell sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 1, whiteSpace: 'nowrap' }}>
                                                {req.status === 'pending' && (
                                                    <>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => handleApprove(req.id)}
                                                            disabled={processingId !== null}
                                                            sx={{
                                                                textTransform: 'none',
                                                                fontSize: '0.75rem',
                                                                borderRadius: 1,
                                                                borderColor: MS_COLORS.accent,
                                                                color: MS_COLORS.accent,
                                                                '&:hover': { backgroundColor: MS_COLORS.accent + '10', borderColor: MS_COLORS.accent },
                                                                '&:disabled': { borderColor: MS_COLORS.border, color: MS_COLORS.textSecondary },
                                                            }}
                                                        >
                                                            {processingId === req.id ? (
                                                                <CircularProgress size={14} sx={{ mr: 0.5, color: MS_COLORS.accent }} />
                                                            ) : (
                                                                <CheckCircle sx={{ mr: 0.5, fontSize: 'small' }} />
                                                            )}
                                                            Approve
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => handleRejectClick(req.id)}
                                                            disabled={processingId !== null}
                                                            sx={{
                                                                textTransform: 'none',
                                                                fontSize: '0.75rem',
                                                                borderRadius: 1,
                                                                borderColor: MS_COLORS.red,
                                                                color: MS_COLORS.red,
                                                                '&:hover': { backgroundColor: MS_COLORS.red + '10', borderColor: MS_COLORS.red },
                                                                '&:disabled': { borderColor: MS_COLORS.border, color: MS_COLORS.textSecondary },
                                                            }}
                                                        >
                                                            <Cancel sx={{ mr: 0.5, fontSize: 'small' }} />
                                                            Reject
                                                        </Button>
                                                    </>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={requests.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiTablePagination-actions': {
                                fontSize: '0.8rem',
                                color: MS_COLORS.textSecondary,
                            },
                        }}
                    />
                </>
            )}

            {/* Reject Dialog */}
            <Dialog
                open={rejectOpen}
                onClose={() => !processingId && setRejectOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: '8px', p: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}
            >
                <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.2rem', color: MS_COLORS.text }}>
                    Reject Unsubscribe Request
                </DialogTitle>
                <Divider sx={{ mb: 2 }} />
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2, color: MS_COLORS.textSecondary }}>
                        The donor will be notified that their request was not approved. Their subscription remains active.
                    </Typography>
                    <TextField
                        label="Admin Notes (optional)"
                        multiline
                        rows={3}
                        fullWidth
                        value={rejectNotes}
                        onChange={(e) => setRejectNotes(e.target.value)}
                        variant="outlined"
                        margin="normal"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                                '& fieldset': { borderColor: MS_COLORS.border },
                                '&:hover fieldset': { borderColor: MS_COLORS.primary },
                                '&.Mui-focused fieldset': { borderColor: MS_COLORS.primary, borderWidth: 2 },
                            },
                            '& .MuiInputBase-input': { fontSize: '0.9rem', color: MS_COLORS.text },
                            '& .MuiInputLabel-root': { fontSize: '0.9rem', color: MS_COLORS.textSecondary },
                        }}
                    />
                </DialogContent>
                <Divider sx={{ mt: 2 }} />
                <DialogActions sx={{ justifyContent: 'flex-end', pt: 2, px: 3 }}>
                    <Button
                        onClick={() => setRejectOpen(false)}
                        disabled={!!processingId}
                        variant="outlined"
                        sx={{
                            borderRadius: '4px',
                            fontSize: '0.85rem',
                            textTransform: 'none',
                            borderColor: MS_COLORS.border,
                            color: MS_COLORS.text,
                            '&:hover': { borderColor: MS_COLORS.primary, backgroundColor: MS_COLORS.primaryLight + '30' },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleRejectSubmit}
                        disabled={!!processingId}
                        variant="contained"
                        sx={{
                            textTransform: 'none',
                            borderRadius: '4px',
                            backgroundColor: MS_COLORS.red,
                            color: MS_COLORS.white,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            minWidth: 100,
                            '&:hover': { backgroundColor: MS_COLORS.red, opacity: 0.9 },
                            '&:disabled': { backgroundColor: MS_COLORS.border, color: MS_COLORS.textSecondary },
                        }}
                    >
                        {processingId === selectedId ? (
                            <>
                                <CircularProgress size={16} sx={{ color: MS_COLORS.white, mr: 1 }} />
                                Rejecting...
                            </>
                        ) : 'Reject'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UnsubscribeRequestsTable;
